import {
  faArrowLeft,
  faBars,
  faCreditCard,
  faSpinner,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Divider,
  Radio,
  Select,
  Space,
  Typography,
  notification,
} from "antd";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "../../css/JuanScope/ScopeRegistration1.css";
import useEnrollmentStatus from "../../hooks/userEnrollmentStatus";
import SJDEFILogo from "../../images/SJDEFILogo.png";
import SideNavigation from "./SideNavigation";
import { createTuitionPayment } from "../../api/applicant";
import amexLogo from "../../utils/amex.png";
import gcashLogo from "../../utils/gcash.png";
import jcbLogo from "../../utils/jcb.png";
import mastercardLogo from "../../utils/mastercard.png";
import visaLogo from "../../utils/visa.png";

const { Title, Text } = Typography;

const CardBrands = () => (
  <Space size="small">
    <img src={visaLogo} alt="Visa" style={{ height: "20px" }} />
    <img src={mastercardLogo} alt="Mastercard" style={{ height: "20px" }} />
    <img src={jcbLogo} alt="JCB" style={{ height: "20px" }} />
    <img src={amexLogo} alt="Amex" style={{ height: "20px" }} />
  </Space>
);

const GCashLogo = () => (
  <img src={gcashLogo} alt="GCash" style={{ height: "20px" }} />
);

function SelectPaymentMethod() {
  const navigate = useNavigate();
  const location = useLocation();

  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [paymentMode, setPaymentMode] = useState(() => {
    const { paymentModes } = location.state || {};
    return paymentModes?.installmentPayment ? "Installment Plan" : "Full Payment";
  });
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState(null);

  const { userData, applicationStatus, loading, error, handleLogout } =
    useEnrollmentStatus();

  const PAYMENT_MODE_OPTIONS = useMemo(() => {
    const { paymentModes } = location.state || {};
    const options = [
      { value: "Full Payment", label: "Full Payment" },
    ];
    if (paymentModes?.installmentPayment) {
      options.push({ value: "Installment Plan", label: "Installment Plan" });
    }
    return options;
  }, [location.state]);

  // Check if total amount is zero
  const isZeroAmount = useMemo(() => {
    const { tuitionAndFees } = location.state || {};
    return tuitionAndFees?.totalAmount === 0;
  }, [location.state]);

  useEffect(() => {
    if (paymentError) {
      setPaymentError(null);
    }
  }, [paymentMode, paymentMethod, agreeToTerms]);

  // Handle zero amount case on component mount
  useEffect(() => {
    if (isZeroAmount) {
      handleZeroAmountPayment();
    }
  }, [isZeroAmount]);

  const toggleSidebar = useCallback(() => setSidebarOpen(!sidebarOpen), [sidebarOpen]);
  const closeSidebar = useCallback(() => setSidebarOpen(false), []);

  const handleBack = useCallback(() => {
    console.log('🔄 Navigating back');
    navigate(-1);
  }, [navigate]);

  const preparePaymentData = useCallback(() => {
    console.log('📝 Preparing payment data...');
    console.log('📍 Location state:', location.state);

    try {
      const { studentInfo, tuitionAndFees, installmentPlan } = location.state || {};
      
      if (!studentInfo || !tuitionAndFees) {
        throw new Error('Missing required data from navigation state');
      }

      const currentPaymentAmount = paymentMode === "Full Payment" 
        ? tuitionAndFees.totalAmount 
        : (installmentPlan && installmentPlan.length > 0 ? installmentPlan[0].amount : 0);

      console.log('💰 Payment amount calculated:', currentPaymentAmount);
      console.log('🎯 Payment mode:', paymentMode);
      console.log('💳 Payment method:', paymentMethod);

      const paymentData = {
        studentInfo: {
          ...studentInfo,
          email: studentInfo.email?.toLowerCase(),
          studentID: studentInfo.studentID,
          fullName: studentInfo.fullName,
          mobile: studentInfo.mobile,
          dob: studentInfo.dob,
          gender: studentInfo.gender,
          academicLevel: studentInfo.academicLevel,
          academicStrand: studentInfo.academicStrand,
          academicTerm: studentInfo.academicTerm,
          academicYear: studentInfo.academicYear,
          voucherType: studentInfo.voucherType,
          hasReservation: studentInfo.hasReservation || false,
        },
        tuitionAndFees: {
          tuition: tuitionAndFees.tuition || 0,
          miscellaneous: tuitionAndFees.miscellaneous || 0,
          otherFees: tuitionAndFees.otherFees || 0,
          energyFee: tuitionAndFees.energyFee || 0,
          totalAmount: tuitionAndFees.totalAmount || 0,
          voucherDeduction: tuitionAndFees.voucherDeduction || 0,
          voucherType: tuitionAndFees.voucherType,
          semester: tuitionAndFees.semester || "1st_sem",
        },
        paymentMode: paymentMode,
        paymentMethod: isZeroAmount ? "none" : paymentMethod,
        paymentAmount: currentPaymentAmount,
        isFullPayment: paymentMode === "Full Payment",
        installmentPlan: paymentMode === "Installment Plan" ? (installmentPlan || []) : [],
        remainingPayments: paymentMode === "Installment Plan" ? (installmentPlan?.slice(1) || []) : [],
        agreeToTerms: isZeroAmount ? true : agreeToTerms,
        timestamp: new Date().toISOString(),
        paymentType: paymentMode === "Full Payment" ? "full" : "downpayment",
      };

      console.log('✅ Payment data prepared successfully:', paymentData);
      return paymentData;
    } catch (error) {
      console.error('❌ Error preparing payment data:', error);
      throw error;
    }
  }, [location.state, paymentMode, paymentMethod, agreeToTerms, isZeroAmount]);

  const handleZeroAmountPayment = useCallback(async () => {
    console.log('🚀 Handling zero amount payment...');
    try {
      setIsProcessingPayment(true);
      setPaymentError(null);

      const paymentData = preparePaymentData();
      console.log('📦 Zero amount payment data:', JSON.stringify(paymentData, null, 2));

      notification.info({
        message: 'Processing Payment',
        description: 'Recording your payment status, please wait...',
        placement: 'topRight',
        duration: 3,
      });

      const result = await createTuitionPayment(paymentData);

      if (result.success) {
        localStorage.setItem('paymentCompleted', 'true');
        notification.success({
          message: 'Payment Recorded',
          description:
            'No payment is required as the amount due is zero. Your payment status has been recorded.',
          placement: 'topRight',
          duration: 4,
        });

        setTimeout(() => {
          console.log('🔄 Redirecting to dashboard...');
          navigate('/dashboard'); // Adjust the route as per your app
        }, 1500);
      } else {
        throw new Error(result.message || 'Failed to record payment');
      }
    } catch (error) {
      console.error('❌ Zero amount payment error:', error);
      setPaymentError(error.message);
      notification.error({
        message: 'Payment Recording Failed',
        description: error.message || 'An unexpected error occurred. Please try again.',
        placement: 'topRight',
        duration: 6,
      });
    } finally {
      setIsProcessingPayment(false);
    }
  }, [navigate, preparePaymentData]);

  const handlePaymentProcessing = useCallback(async () => {
    console.log('🚀 Starting payment processing...');
    console.log('📊 Form validation - Payment method:', paymentMethod, 'Terms agreed:', agreeToTerms);

    if (!paymentMethod || !agreeToTerms) {
      const missingFields = [];
      if (!paymentMethod) missingFields.push('payment method');
      if (!agreeToTerms) missingFields.push('terms agreement');
      
      console.warn('⚠️ Form validation failed. Missing:', missingFields.join(', '));
      notification.warning({
        message: 'Incomplete Form',
        description: `Please select a ${missingFields.join(' and ')}.`,
        placement: 'topRight',
      });
      return;
    }

    try {
      setIsProcessingPayment(true);
      setPaymentError(null);
      console.log('⏳ Setting loading state...');

      const paymentData = preparePaymentData();
      console.log('📦 Final payment data to be sent:', JSON.stringify(paymentData, null, 2));

      notification.info({
        message: 'Processing Payment',
        description: 'Creating your payment link, please wait...',
        placement: 'topRight',
        duration: 3,
      });

      console.log('🌐 Calling createTuitionPayment API...');
      const result = await createTuitionPayment(paymentData);
      
      console.log('✅ Payment API response:', result);

      if (result.success && result.data?.checkoutUrl) {
        console.log('🎉 Payment link created successfully!');
        console.log('🔗 Checkout URL:', result.data.checkoutUrl);
        console.log('📄 Reference Number:', result.data.referenceNumber);

        localStorage.setItem('paymentCompleted', 'true');
        
        notification.success({
          message: 'Payment Link Created',
          description: `Reference: ${result.data.referenceNumber}. Redirecting to payment gateway...`,
          placement: 'topRight',
          duration: 4,
        });

        setTimeout(() => {
          console.log('🔄 Redirecting to PayMongo checkout...');
          window.location.href = result.data.checkoutUrl;
        }, 1500);
      } else {
        throw new Error(result.message || 'Failed to create payment link');
      }
    } catch (error) {
      console.error('❌ Payment processing error:', error);
      console.error('📊 Error details:', {
        message: error.message,
        stack: error.stack,
        timestamp: new Date().toISOString(),
      });

      setPaymentError(error.message);

      notification.error({
        message: 'Payment Processing Failed',
        description: error.message || 'An unexpected error occurred. Please try again.',
        placement: 'topRight',
        duration: 6,
      });
    } finally {
      setIsProcessingPayment(false);
      console.log('🏁 Payment processing completed, loading state cleared');
    }
  }, [paymentMethod, agreeToTerms, preparePaymentData]);

  const handleNext = useCallback(async () => {
    console.log('👆 Next button clicked');
    await handlePaymentProcessing();
  }, [handlePaymentProcessing]);

  const formatCurrency = useCallback((amount) => {
    if (!amount && amount !== 0) return "₱0.00";
    return `₱${amount.toLocaleString("en-PH", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }, []);

  const isFormValid = paymentMethod && agreeToTerms && !isProcessingPayment;

  console.log('🔍 Form validation state:', {
    paymentMethod,
    agreeToTerms,
    isProcessingPayment,
    isFormValid,
  });

  const getCurrentPaymentAmount = useCallback(() => {
    try {
      const { installmentPlan, tuitionAndFees, paymentModes } = location.state || {};
      
      if (paymentMode === "Full Payment") {
        return tuitionAndFees?.totalAmount || 0;
      } else {
        return paymentModes?.installmentPayment && installmentPlan && installmentPlan.length > 0 
          ? installmentPlan[0].amount 
          : 0;
      }
    } catch (error) {
      console.error('Error getting payment amount:', error);
      return 0;
    }
  }, [location.state, paymentMode]);

  if (isZeroAmount) {
    return (
      <div className="scope-registration-container">
        <header className="juan-register-header">
          <div className="juan-header-left">
            <img
              src={SJDEFILogo}
              alt="SJDEFI Logo"
              className="juan-logo-register"
            />
            <div className="juan-header-text">
              <Title level={1} style={{ margin: 0, color: "inherit" }}>
                JUAN SCOPE
              </Title>
            </div>
          </div>
          <div className="hamburger-menu">
            <Button
              type="text"
              icon={<FontAwesomeIcon icon={sidebarOpen ? faTimes : faBars} />}
              onClick={toggleSidebar}
              aria-label="Toggle navigation menu"
              size="large"
              disabled={isProcessingPayment}
            />
          </div>
        </header>

        <div className="scope-registration-content">
          <SideNavigation
            userData={userData}
            onNavigate={closeSidebar}
            isOpen={sidebarOpen}
            {...applicationStatus}
          />

          <main
            className={`scope-main-content ${sidebarOpen ? "sidebar-open" : ""}`}
          >
            <div className="registration-content">
              <Title level={2} className="registration-title">
                Tuition Payment
              </Title>
              <Divider />

              <div className="registration-container">
                {isProcessingPayment && (
                  <Alert
                    message="Processing Payment"
                    description="Please wait while we record your payment status. Do not close this page."
                    type="info"
                    showIcon
                    icon={<FontAwesomeIcon icon={faSpinner} spin />}
                    style={{ marginBottom: "2rem" }}
                  />
                )}

                {paymentError && (
                  <Alert
                    message="Payment Error"
                    description={paymentError}
                    type="error"
                    showIcon
                    closable
                    onClose={() => setPaymentError(null)}
                    style={{ marginBottom: "2rem" }}
                  />
                )}

                <Alert
                  message="No Payment Required"
                  description="Your tuition has been fully covered by the reservation fee or voucher. Your payment status is being recorded."
                  type="success"
                  showIcon
                  style={{ marginBottom: "2rem" }}
                />

                <div className="form-buttons" style={{ display: "flex", justifyContent: "flex-start", marginTop: "2rem" }}>
                  <Button
                    size="large"
                    onClick={handleBack}
                    icon={<FontAwesomeIcon icon={faArrowLeft} />}
                    style={{ minWidth: "120px" }}
                    disabled={isProcessingPayment}
                  >
                    Back
                  </Button>
                </div>
              </div>
            </div>
          </main>
        </div>

        {sidebarOpen && (
          <div className="sidebar-overlay active" onClick={toggleSidebar}></div>
        )}

        {showLogoutModal && (
          <div className="scope-modal-overlay">
            <div className="scope-confirm-modal">
              <Title level={3}>Confirm Logout</Title>
              <Text>Are you sure you want to logout?</Text>
              <div className="scope-modal-buttons" style={{ marginTop: "1rem" }}>
                <Space>
                  <Button 
                    onClick={() => setShowLogoutModal(false)}
                    disabled={isProcessingPayment}
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="primary" 
                    danger 
                    onClick={handleLogout}
                    disabled={isProcessingPayment}
                  >
                    Logout
                  </Button>
                </Space>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="scope-registration-container">
      <header className="juan-register-header">
        <div className="juan-header-left">
          <img
            src={SJDEFILogo}
            alt="SJDEFI Logo"
            className="juan-logo-register"
          />
          <div className="juan-header-text">
            <Title level={1} style={{ margin: 0, color: "inherit" }}>
              JUAN SCOPE
            </Title>
          </div>
        </div>
        <div className="hamburger-menu">
          <Button
            type="text"
            icon={<FontAwesomeIcon icon={sidebarOpen ? faTimes : faBars} />}
            onClick={toggleSidebar}
            aria-label="Toggle navigation menu"
            size="large"
            disabled={isProcessingPayment}
          />
        </div>
      </header>

      <div className="scope-registration-content">
        <SideNavigation
          userData={userData}
          onNavigate={closeSidebar}
          isOpen={sidebarOpen}
          {...applicationStatus}
        />

        <main
          className={`scope-main-content ${sidebarOpen ? "sidebar-open" : ""}`}
        >
          <div className="registration-content">
            <Title level={2} className="registration-title">
              Tuition Payment
            </Title>
            <Divider />

            <div className="registration-container">
              <Alert
                message="Complete your payment until August 23, 2025"
                description="The 'Tuition Payment' section marks the final step to officially secure your place as a student. Complete your payment until August 23, 2025 and take the last exciting step toward beginning your journey with us!"
                type="warning"
                showIcon
                style={{ marginBottom: "2rem" }}
              />

              {paymentError && (
                <Alert
                  message="Payment Error"
                  description={paymentError}
                  type="error"
                  showIcon
                  closable
                  onClose={() => setPaymentError(null)}
                  style={{ marginBottom: "2rem" }}
                />
              )}

              {isProcessingPayment && (
                <Alert
                  message="Processing Payment"
                  description="Please wait while we create your payment link. Do not close this page."
                  type="info"
                  showIcon
                  icon={<FontAwesomeIcon icon={faSpinner} spin />}
                  style={{ marginBottom: "2rem" }}
                />
              )}

              <div className="personal-info-section">
                <Space
                  direction="vertical"
                  size="large"
                  style={{ width: "100%" }}
                >
                  <div>
                    <Text
                      strong
                      style={{ display: "block", marginBottom: "0.5rem" }}
                    >
                      Select Payment Mode:
                    </Text>
                    <Select
                      value={paymentMode}
                      onChange={setPaymentMode}
                      options={PAYMENT_MODE_OPTIONS}
                      style={{ width: "100%" }}
                      size="large"
                      disabled={isProcessingPayment}
                    />
                  </div>

                  <Card
                    title={
                      <Space>
                        <FontAwesomeIcon icon={faCreditCard} />
                        <Text strong>Payment Summary</Text>
                      </Space>
                    }
                    style={{ marginBottom: "1rem" }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        backgroundColor: "#f5f5f5",
                        padding: "12px 16px",
                        borderRadius: "6px",
                        fontWeight: "600",
                        marginBottom: "0.5rem",
                      }}
                    >
                      <Text strong>
                        {paymentMode === "Full Payment" ? "Full Payment" : "Downpayment (1/3)"}
                      </Text>
                      <Text strong style={{ color: "#1890ff" }}>
                        {formatCurrency(getCurrentPaymentAmount())}
                      </Text>
                    </div>
                    {paymentMode === "Installment Plan" && (
                      <Text type="secondary">
                        Note: Installment plan includes a 6% increase on the total amount.
                      </Text>
                    )}
                  </Card>

                  <div>
                    <Text
                      strong
                      style={{ display: "block", marginBottom: "1rem" }}
                    >
                      Choose Payment Method:
                    </Text>
                    <Radio.Group
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      value={paymentMethod}
                      style={{ width: "100%" }}
                      disabled={isProcessingPayment}
                    >
                      <Space
                        direction="vertical"
                        size="middle"
                        style={{ width: "100%" }}
                      >
                        <Radio
                          value="card"
                          style={{
                            padding: "16px",
                            border: "1px solid #d9d9d9",
                            borderRadius: "8px",
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                            marginRight: 0,
                            opacity: isProcessingPayment ? 0.6 : 1,
                          }}
                        >
                          <Space>
                            <Text>Credit/Debit Card</Text>
                            <CardBrands />
                          </Space>
                        </Radio>
                        <Radio
                          value="gcash"
                          style={{
                            padding: "16px",
                            border: "1px solid #d9d9d9",
                            borderRadius: "8px",
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                            marginRight: 0,
                            opacity: isProcessingPayment ? 0.6 : 1,
                          }}
                        >
                          <Space>
                            <Text>GCash</Text>
                            <GCashLogo />
                          </Space>
                        </Radio>
                      </Space>
                    </Radio.Group>
                  </div>

                  <Card style={{ backgroundColor: "#fafafa" }}>
                    <Space direction="vertical" size="small">
                      <Text>
                        Before proceeding to payment, please read the full Terms
                        & Conditions and Data Privacy Policy:
                      </Text>
                      <Checkbox
                        checked={agreeToTerms}
                        onChange={(e) => setAgreeToTerms(e.target.checked)}
                        disabled={isProcessingPayment}
                      >
                        I agree to the{" "}
                        <a href="#" style={{ color: "#1890ff" }}>
                          Terms & Conditions
                        </a>{" "}
                        and{" "}
                        <a href="#" style={{ color: "#1890ff" }}>
                          Data Privacy Policy
                        </a>
                        .
                      </Checkbox>
                    </Space>
                  </Card>

                  <div
                    className="form-buttons"
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: "1rem",
                      marginTop: "2rem",
                    }}
                  >
                    <Button
                      size="large"
                      onClick={handleBack}
                      icon={<FontAwesomeIcon icon={faArrowLeft} />}
                      style={{ minWidth: "120px" }}
                      disabled={isProcessingPayment}
                    >
                      Back
                    </Button>
                    <Button
                      type="primary"
                      size="large"
                      onClick={handleNext}
                      disabled={!isFormValid}
                      loading={isProcessingPayment}
                      icon={isProcessingPayment ? <FontAwesomeIcon icon={faSpinner} spin /> : null}
                      style={{
                        minWidth: "180px",
                        backgroundColor: isFormValid && !isProcessingPayment ? "#52c41a" : undefined,
                        borderColor: isFormValid && !isProcessingPayment ? "#52c41a" : undefined,
                      }}
                    >
                      {isProcessingPayment ? "Processing..." : "Proceed to Payment"}
                    </Button>
                  </div>
                </Space>
              </div>
            </div>
          </div>
        </main>
      </div>

      {sidebarOpen && (
        <div className="sidebar-overlay active" onClick={toggleSidebar}></div>
      )}

      {showLogoutModal && (
        <div className="scope-modal-overlay">
          <div className="scope-confirm-modal">
            <Title level={3}>Confirm Logout</Title>
            <Text>Are you sure you want to logout?</Text>
            <div className="scope-modal-buttons" style={{ marginTop: "1rem" }}>
              <Space>
                <Button 
                  onClick={() => setShowLogoutModal(false)}
                  disabled={isProcessingPayment}
                >
                  Cancel
                </Button>
                <Button 
                  type="primary" 
                  danger 
                  onClick={handleLogout}
                  disabled={isProcessingPayment}
                >
                  Logout
                </Button>
              </Space>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SelectPaymentMethod;